/**
 * The "Your data" section of the settings surface — the self-serve EXPORT and
 * DELETE controls (compliance.md § "Data rights"), the in-product half of what
 * the marketing site already promises.
 *
 * Pure presentation over `@4f/ui` primitives + the injected
 * {@link DataRightsStore} (DIP). Deletion is a TWO-STEP control: the first press
 * only reveals the warning + a confirm button, because the request signs the
 * account out immediately and cannot be undone from inside the product. Every
 * user-facing string is a `settings.dataRights.*` i18n key.
 */

import { Button, useT } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { DataRightsStore } from '../../stores/data-rights.ts';

export interface DataRightsPanelProps {
  readonly store: DataRightsStore;
  /** Renders an ISO instant in the user's own timezone (injected by the shell). */
  formatDate(iso: string): string;
  /**
   * Ends the session. Called ONLY from the confirmation the user reads after a
   * successful deletion — never automatically: dropping the session unmounts this
   * panel, so an automatic sign-out would erase the purge date before it could be
   * read, and the user cannot sign back in to find it.
   */
  onSignOut(): void;
}

export function DataRightsPanel(props: DataRightsPanelProps): JSX.Element {
  const { t } = useT();
  const [confirming, setConfirming] = createSignal(false);

  const scheduled = (): boolean => props.store.deletion() !== null;

  return (
    <section class="f4-settings__section" aria-labelledby="f4-settings-data-rights">
      <h3 id="f4-settings-data-rights" class="f4-settings__legend">
        {t('settings.dataRights.title')}
      </h3>
      <p class="f4-genui__hint">{t('settings.dataRights.description')}</p>

      <div class="f4-settings__actions">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={props.store.busy() || scheduled()}
          onClick={() => void props.store.exportData()}
        >
          {t('settings.dataRights.export')}
        </Button>

        <Show
          when={confirming()}
          fallback={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={props.store.busy() || scheduled()}
              onClick={() => setConfirming(true)}
            >
              {t('settings.dataRights.delete')}
            </Button>
          }
        >
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={props.store.busy() || scheduled()}
            onClick={() => void props.store.requestDeletion()}
          >
            {t('settings.dataRights.deleteConfirm')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={props.store.busy()}
            onClick={() => setConfirming(false)}
          >
            {t('settings.dataRights.deleteCancel')}
          </Button>
        </Show>
      </div>

      {/* Shown the moment the destructive path is armed — the user must read what
          it costs (immediate sign-out, no in-product undo) BEFORE confirming. */}
      <Show when={confirming() && !scheduled()}>
        <p class="f4-genui__error" role="alert">
          {t('settings.dataRights.deleteWarning')}
        </p>
      </Show>

      <Show when={props.store.busy()}>
        <p class="f4-genui__hint" role="status">
          {t('settings.dataRights.working')}
        </p>
      </Show>

      <Show when={props.store.savedAs()}>
        {(fileName) => (
          <p class="f4-genui__hint" role="status">
            {t('settings.dataRights.exported', { file: fileName() })}
          </p>
        )}
      </Show>

      <Show when={props.store.deletion()}>
        {(schedule) => (
          <>
            <p class="f4-genui__hint" role="status">
              {t('settings.dataRights.scheduled', {
                date: props.formatDate(schedule().purgeEligibleAt),
              })}
            </p>
            <div class="f4-settings__actions">
              <Button type="button" variant="primary" size="sm" onClick={() => props.onSignOut()}>
                {t('settings.dataRights.signOut')}
              </Button>
            </div>
          </>
        )}
      </Show>

      <Show when={props.store.error()}>
        {(error) => (
          <p class="f4-genui__error" role="alert">
            {error().code === 'too_many_requests'
              ? t('settings.dataRights.cooldown')
              : error().message}
          </p>
        )}
      </Show>
    </section>
  );
}
